"use client";

import { useEffect, useMemo, useState } from "react";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import QuoteContainer from "./QuoteContainer";
import SecondaryButton from "../../Components/SecondaryButton";
import axios from "axios";
import {
  TbSearch,
  TbX,
  TbFilter,
  TbChevronDown,
  TbCircleCheck,
  TbAlertCircle,
  TbInfoCircle,
  TbClipboardList,
  TbTruckDelivery,
  TbClockExclamation,
  TbCurrencyDollar,
  TbCar,
  TbTruck,
} from "react-icons/tb";
import { ChevronLeft, ChevronRight } from "lucide-react";

function Snackbar({ message, type = "success", onClose, duration = 5000 }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColor = {
    success: "bg-green-600 border-green-700",
    error: "bg-red-600 border-red-700",
    info: "bg-blue-600 border-blue-700",
    warning: "bg-amber-600 border-amber-700",
  }[type];

  const icon = {
    success: <TbCircleCheck className="w-5 h-5" />,
    error: <TbAlertCircle className="w-5 h-5" />,
    info: <TbInfoCircle className="w-5 h-5" />,
    warning: <TbInfoCircle className="w-5 h-5" />,
  }[type];

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 transform transition-all duration-300 ${
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      }`}
    >
      <div
        className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg border max-w-sm flex items-start gap-3`}
      >
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="flex-shrink-0 text-white/80 hover:text-white ml-2"
        >
          <TbX className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

function formatLocation(city, state, zipcode) {
  const cityState = [city, state].filter(Boolean).join(", ");
  if (zipcode) {
    return cityState ? `${cityState} ${zipcode}` : zipcode;
  }
  return cityState || "N/A";
}

function parsePaginator(apiPayload) {
  if (
    apiPayload?.data &&
    typeof apiPayload.data === "object" &&
    Array.isArray(apiPayload.data.data)
  ) {
    return apiPayload.data;
  }

  return null;
}

function mapQuoteFromApi(quote) {
  const vehiclesSource = Array.isArray(quote?.quote_vehicles)
    ? quote.quote_vehicles
    : Array.isArray(quote?.quoteVehicles)
    ? quote.quoteVehicles
    : [];

  const loadTransport = quote?.load_transport || quote?.loadTransport || null;

  return {
    id: quote?.id ?? "",
    user: quote?.name ?? "—",
    name: quote?.name ?? "—",
    email: quote?.email ?? "",
    phone: quote?.phone ?? "",
    payment_rate: quote?.payment_rate ?? null,
    maximum_budget: quote?.maximum_budget ?? null,
    price_low: quote?.price_low ?? null,
    price_medium: quote?.price_medium ?? null,
    price_high: quote?.price_high ?? null,
    original_distance_text: quote?.original_distance_text ?? "",
    original_distance_miles: quote?.original_distance_miles ?? null,
    original_duration_text: quote?.original_duration_text ?? "",
    original_duration_value: quote?.original_duration_value ?? null,
    original_driver_duration_text: quote?.original_driver_duration_text ?? "",
    original_driver_duration: quote?.original_driver_duration ?? null,
    effective_distance_miles: quote?.effective_distance_miles ?? null,
    effective_distance_text: quote?.effective_distance_text ?? "",
    effective_duration_value: quote?.effective_duration_value ?? null,
    effective_duration_text: quote?.effective_duration_text ?? "",
    effective_driver_duration_value: quote?.effective_driver_duration_value ?? null,
    effective_driver_duration_text: quote?.effective_driver_duration_text ?? "",
    pickup_address: formatLocation(
      quote?.pickup_city,
      quote?.pickup_state,
      quote?.pickup_zipcode
    ),
    delivery_address: formatLocation(
      quote?.delivery_city,
      quote?.delivery_state,
      quote?.delivery_zipcode
    ),
    pickup_city: quote?.pickup_city ?? "",
    pickup_state: quote?.pickup_state ?? "",
    pickup_zipcode: quote?.pickup_zipcode ?? "",
    delivery_city: quote?.delivery_city ?? "",
    delivery_state: quote?.delivery_state ?? "",
    delivery_zipcode: quote?.delivery_zipcode ?? "",
    pickup_date: quote?.pickup_date ?? "",
    created_at: quote?.created_at ?? "",
    updated_at: quote?.updated_at ?? "",
    status: quote?.status ?? "",
    transport_type: quote?.transport_type ?? "",
    has_load_transport: !!loadTransport,
    load_transport_id: loadTransport?.id ?? null,
    load_transport_order_id: loadTransport?.order_id ?? null,
    vehicles: vehiclesSource.map((vehicle) => ({
      id: vehicle?.id ?? "",
      type: vehicle?.type ?? "",
      year: vehicle?.year ?? "",
      make: vehicle?.make ?? "",
      model: vehicle?.model ?? "",
      operable: Boolean(vehicle?.operable),
    })),
    original: quote,
  };
}

function KpiCard({
  title,
  value,
  icon,
  valueClassName = "text-white",
  children,
}) {
  return (
    <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs text-gray-400 uppercase tracking-wider">
            {title}
          </div>

          <div className={`mt-2 text-lg sm:text-xl font-bold ${valueClassName}`}>
            {Number(value || 0)}
          </div>
        </div>

        <div className="h-10 w-10 rounded-xl border border-zinc-600 bg-black/20 flex items-center justify-center text-xl flex-shrink-0">
          {icon}
        </div>
      </div>

      {children && (
        <div className="mt-3 text-xs text-white/70">
          {children}
        </div>
      )}
    </div>
  );
}

export default function Quotes() {
  const API_ORIGIN = useMemo(() => {
    return process.env.REACT_APP_API_ORIGIN || "https://new.cajuu.app";
  }, []);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination backend
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [rangeFrom, setRangeFrom] = useState(0);
  const [rangeTo, setRangeTo] = useState(0);
  const [perPage, setPerPage] = useState(20);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const [makeFilter, setMakeFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [operableFilter, setOperableFilter] = useState("all");

  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Sorting
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // Collapsible filters
  const [filtersOpen, setFiltersOpen] = useState(false);

  // KPI
  const [kpis, setKpis] = useState({
    totalQuotes: 0,
    quotesWithLoadTransport: 0,
    quotesWithoutLoadTransport: 0,
    quotesWithDefinedPrices: 0,
    openTransportCount: 0,
    enclosedTransportCount: 0,
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const showSnackbar = (message, type = "success") => {
    setSnackbar({ open: true, message, type });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const getToken = () => localStorage.getItem("token");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 450);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const buildServerParams = (page) => {
    const params = {
      page: page || 1,
      per_page: perPage || 20,
      sort_by: sortBy || "created_at",
      sort_direction: sortDirection || "desc",
      with_kpis: 1,
    };

    const q = (debouncedSearchQuery || "").trim();
    if (q) params.q = q;

    if (makeFilter.trim()) params.make = makeFilter.trim();
    if (modelFilter.trim()) params.model = modelFilter.trim();
    if (yearFilter.trim()) params.year = yearFilter.trim();

    if (operableFilter !== "all") {
      params.operable = operableFilter;
    }

    if (nameFilter.trim()) params.name = nameFilter.trim();
    if (emailFilter.trim()) params.email = emailFilter.trim();
    if (phoneFilter.trim()) params.phone = phoneFilter.trim();

    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    return params;
  };

  const fetchQuotes = async (page = 1) => {
    setLoading(true);

    try {
      const token = getToken();

      const response = await axios.get(`${API_ORIGIN}/api/quote/paginated`, {
        headers: {
          "Screen-Name": "/Quotes",
          Authorization: `Bearer ${token}`,
        },
        params: buildServerParams(page),
      });

      const paginator = parsePaginator(response.data);
      const list = Array.isArray(paginator?.data) ? paginator.data : [];
      const mapped = list.map(mapQuoteFromApi);

      const cp = Number(paginator?.current_page ?? page) || 1;
      const lp = Number(paginator?.last_page ?? 1) || 1;
      const tt = Number(paginator?.total ?? mapped.length) || 0;
      const pp = Number(paginator?.per_page ?? perPage) || perPage;

      setItems(mapped);
      setCurrentPage(cp);
      setTotalPages(lp);
      setTotalItems(tt);
      setPerPage(pp);

      setRangeFrom(Number(paginator?.from ?? (mapped.length ? 1 : 0)) || 0);
      setRangeTo(Number(paginator?.to ?? mapped.length) || 0);

      const serverKpis = paginator?.kpis;
      if (serverKpis && typeof serverKpis === "object") {
        setKpis({
          totalQuotes: Number(serverKpis.totalQuotes ?? 0),
          quotesWithLoadTransport: Number(
            serverKpis.quotesWithLoadTransport ?? 0
          ),
          quotesWithoutLoadTransport: Number(
            serverKpis.quotesWithoutLoadTransport ?? 0
          ),
          quotesWithDefinedPrices: Number(
            serverKpis.quotesWithDefinedPrices ??
            serverKpis.quotesWithPaymentRate ??
            0
          ),
          openTransportCount: Number(serverKpis.openTransportCount ?? 0),
          enclosedTransportCount: Number(serverKpis.enclosedTransportCount ?? 0),
        });
      } else {
        setKpis({
          totalQuotes: mapped.length,
          quotesWithLoadTransport: mapped.filter(
            (quote) => quote.has_load_transport
          ).length,
          quotesWithoutLoadTransport: mapped.filter(
            (quote) => !quote.has_load_transport
          ).length,
          quotesWithDefinedPrices: mapped.filter(
            (quote) =>
              quote.price_low !== null ||
              quote.price_medium !== null ||
              quote.price_high !== null
          ).length,
          openTransportCount: mapped.filter(
            (quote) => String(quote.transport_type) === "Open"
          ).length,
          enclosedTransportCount: mapped.filter(
            (quote) => String(quote.transport_type) === "Enclosed"
          ).length,
        });
      }
    } catch (error) {
      setItems([]);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalItems(0);
      setRangeFrom(0);
      setRangeTo(0);
      setKpis({
        totalQuotes: 0,
        quotesWithLoadTransport: 0,
        quotesWithoutLoadTransport: 0,
        quotesWithDefinedPrices: 0,
        openTransportCount: 0,
        enclosedTransportCount: 0,
      });

      showSnackbar("Error while loading quotes", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    perPage,
    sortBy,
    sortDirection,
    debouncedSearchQuery,
    makeFilter,
    modelFilter,
    yearFilter,
    operableFilter,
    nameFilter,
    emailFilter,
    phoneFilter,
    startDate,
    endDate,
  ]);

  const handleQuoteUpdated = (updatedQuote) => {
    if (!updatedQuote?.id) return;

    setItems((prevItems) =>
      prevItems.map((item) =>
        String(item.id) === String(updatedQuote.id)
          ? {
              ...item,
              ...updatedQuote,
              price_low: updatedQuote.price_low ?? item.price_low,
              price_medium: updatedQuote.price_medium ?? item.price_medium,
              price_high: updatedQuote.price_high ?? item.price_high,
              payment_rate: updatedQuote.payment_rate ?? item.payment_rate,
            }
          : item
      )
    );
  };

  const clearFilters = () => {
    setSearchQuery("");
    setMakeFilter("");
    setModelFilter("");
    setYearFilter("");
    setOperableFilter("all");

    setNameFilter("");
    setEmailFilter("");
    setPhoneFilter("");

    setStartDate("");
    setEndDate("");

    setSortBy("created_at");
    setSortDirection("desc");
    setPerPage(20);

    setCurrentPage(1);
    showSnackbar("Filters cleared", "info");
  };

  const goToPage = (pageNumber) => {
    const target = Math.max(
      1,
      Math.min(Number(pageNumber) || 1, totalPages || 1)
    );
    if (target === currentPage) return;
    setCurrentPage(target);
  };

  const nextPage = () => {
    if (currentPage >= totalPages) return;
    goToPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage <= 1) return;
    goToPage(currentPage - 1);
  };

  const operableOptions = [
    { value: "all", label: "All Conditions" },
    { value: "1", label: "Operable" },
    { value: "0", label: "Inoperable" },
  ];

  return (
    <AuthenticatedLayout>
      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <InfoSection title="QUOTE" description="List of quotes" />
        </GroupedContainer>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
          <KpiCard
            title="Quotes Filtered"
            value={kpis.totalQuotes}
            icon={<TbClipboardList className="text-amber-400" />}
            valueClassName="text-white"
          >
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-1">
                <TbTruck className="text-amber-300" />
                Open:
                <span className="text-amber-300 font-semibold">
                  {Number(kpis.openTransportCount || 0)}
                </span>
              </span>

              <span className="inline-flex items-center gap-1">
                <TbCar className="text-blue-300" />
                Enclosed:
                <span className="text-blue-300 font-semibold">
                  {Number(kpis.enclosedTransportCount || 0)}
                </span>
              </span>
            </div>
          </KpiCard>

          <KpiCard
            title="Converted to Loads"
            value={kpis.quotesWithLoadTransport}
            icon={<TbTruckDelivery className="text-emerald-300" />}
            valueClassName="text-emerald-300"
          />

          <KpiCard
            title="Pending Conversion"
            value={kpis.quotesWithoutLoadTransport}
            icon={<TbClockExclamation className="text-amber-300" />}
            valueClassName="text-amber-300"
          />

          <KpiCard
            title="Prices Defined"
            value={kpis.quotesWithDefinedPrices}
            icon={<TbCurrencyDollar className="text-green-300" />}
            valueClassName="text-green-300"
          />
        </div>

        {/* Filters */}
        <div className="bg-[#1c1b19] p-4 sm:p-6 rounded-xl shadow-sm text-white border border-zinc-600 mt-4 mb-4">
          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={() => setFiltersOpen((prev) => !prev)}
              className="flex items-center gap-2 text-left"
            >
              <TbFilter className="text-amber-500 w-5 h-5" />
              <h2 className="text-lg sm:text-xl font-bold">Filters</h2>
              <TbChevronDown
                className={`text-white w-5 h-5 transition-transform duration-200 ${
                  filtersOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            <button
              onClick={clearFilters}
              className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
            >
              <TbX className="w-4 h-4" />
              Clear Filters
            </button>
          </div>

          {filtersOpen && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                {/* Generic search */}
                <div className="relative lg:col-span-2">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <TbSearch className="text-white text-lg" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search make, model, year, origin, destination, customer..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => {
                        setSearchQuery("");
                        setCurrentPage(1);
                      }}
                      className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                    >
                      <TbX className="text-white" />
                    </button>
                  )}
                </div>

                {/* Make */}
                <input
                  type="text"
                  placeholder="Make"
                  value={makeFilter}
                  onChange={(e) => {
                    setMakeFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full bg-transparent placeholder:text-white py-2 sm:py-3 px-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />

                {/* Model */}
                <input
                  type="text"
                  placeholder="Model"
                  value={modelFilter}
                  onChange={(e) => {
                    setModelFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full bg-transparent placeholder:text-white py-2 sm:py-3 px-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />

                {/* Year */}
                <input
                  type="text"
                  placeholder="Year"
                  value={yearFilter}
                  onChange={(e) => {
                    setYearFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full bg-transparent placeholder:text-white py-2 sm:py-3 px-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />

                {/* Operable */}
                <div className="relative">
                  <select
                    value={operableFilter}
                    onChange={(e) => {
                      setOperableFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-3 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                  >
                    {operableOptions.map((opt) => (
                      <option
                        key={opt.value}
                        value={opt.value}
                        className="bg-[#1c1b19]"
                      >
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <TbChevronDown className="text-white text-lg" />
                  </div>
                </div>

                {/* Name */}
                <input
                  type="text"
                  placeholder="Customer name"
                  value={nameFilter}
                  onChange={(e) => {
                    setNameFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full bg-transparent placeholder:text-white py-2 sm:py-3 px-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />

                {/* Email */}
                <input
                  type="text"
                  placeholder="Customer email"
                  value={emailFilter}
                  onChange={(e) => {
                    setEmailFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full bg-transparent placeholder:text-white py-2 sm:py-3 px-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />

                {/* Phone */}
                <input
                  type="text"
                  placeholder="Customer phone"
                  value={phoneFilter}
                  onChange={(e) => {
                    setPhoneFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full bg-transparent placeholder:text-white py-2 sm:py-3 px-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />

                {/* Start date */}
                <div className="relative">
                  <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">
                    Start date
                  </span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setCurrentPage(1);
                    }}
                    onFocus={(e) => e.target.showPicker?.()}
                    style={{ colorScheme: "dark" }}
                    className="w-full bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                {/* End date */}
                <div className="relative">
                  <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">
                    End date
                  </span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setCurrentPage(1);
                    }}
                    onFocus={(e) => e.target.showPicker?.()}
                    style={{ colorScheme: "dark" }}
                    className="w-full bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                {/* Per page */}
                <div className="relative">
                  <select
                    value={String(perPage)}
                    onChange={(e) => {
                      setPerPage(parseInt(e.target.value, 10));
                      setCurrentPage(1);
                    }}
                    className="w-full pl-3 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                  >
                    <option value="10" className="bg-[#1c1b19]">
                      10 / page
                    </option>
                    <option value="20" className="bg-[#1c1b19]">
                      20 / page
                    </option>
                    <option value="50" className="bg-[#1c1b19]">
                      50 / page
                    </option>
                    <option value="100" className="bg-[#1c1b19]">
                      100 / page
                    </option>
                  </select>
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <TbChevronDown className="text-white text-lg" />
                  </div>
                </div>

                {/* Sort */}
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => {
                      setSortBy(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-3 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                  >
                    <option value="created_at" className="bg-[#1c1b19]">
                      Order by: Created
                    </option>
                    <option value="id" className="bg-[#1c1b19]">
                      Order by: ID
                    </option>
                    <option value="pickup_date" className="bg-[#1c1b19]">
                      Order by: Pickup Date
                    </option>
                    <option value="name" className="bg-[#1c1b19]">
                      Order by: Customer Name
                    </option>
                    <option value="payment_rate" className="bg-[#1c1b19]">
                      Order by: Payment Rate
                    </option>
                    <option value="status" className="bg-[#1c1b19]">
                      Order by: Status
                    </option>
                    <option value="transport_type" className="bg-[#1c1b19]">
                      Order by: Transport Type
                    </option>
                  </select>
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <TbChevronDown className="text-white text-lg" />
                  </div>
                </div>

                {/* Direction */}
                <div className="relative">
                  <select
                    value={sortDirection}
                    onChange={(e) => {
                      setSortDirection(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-3 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                  >
                    <option value="asc" className="bg-[#1c1b19]">
                      Direction: ASC
                    </option>
                    <option value="desc" className="bg-[#1c1b19]">
                      Direction: DESC
                    </option>
                  </select>
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <TbChevronDown className="text-white text-lg" />
                  </div>
                </div>
              </div>

              <div className="mt-3 text-xs text-white/70">
                Server pagination: page {currentPage} of {totalPages} • total{" "}
                {totalItems} • per page {perPage}
              </div>
            </>
          )}
        </div>

        {/* Cards */}
        <div className="mt-6">
          <QuoteContainer data={items} loading={loading} apiOrigin={API_ORIGIN} onQuoteUpdated={handleQuoteUpdated} />
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <>
            <div className="flex items-center justify-center space-x-2 mt-6">
              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={prevPage}
                disabled={currentPage === 1 || loading}
                className="text-white border-white hover:bg-white/10"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </SecondaryButton>

              <div className="flex items-center space-x-1">
                {(() => {
                  let startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, startPage + 4);

                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }

                  const pageNumbers = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <SecondaryButton
                        key={i}
                        size="sm"
                        onClick={() => goToPage(i)}
                        disabled={loading}
                        className={
                          currentPage === i
                            ? "bg-amber-600 text-black hover:bg-white/90"
                            : "text-black border-white hover:bg-white/10"
                        }
                      >
                        {i}
                      </SecondaryButton>
                    );
                  }

                  return pageNumbers;
                })()}
              </div>

              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={nextPage}
                disabled={currentPage === totalPages || loading}
                className="text-white border-white hover:bg-white/10"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </SecondaryButton>
            </div>

            <div className="text-center text-sm text-white/70 mt-2">
              Showing {rangeFrom}-{rangeTo} of {totalItems} quotes
            </div>
          </>
        )}
      </div>

      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={closeSnackbar}
          duration={5000}
        />
      )}
    </AuthenticatedLayout>
  );
}